import { Fragment, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../core';
import { MLPVersion, Position } from '../../types/enums';
import type {
  DynamicObjectInterface,
  PaginationDataInterface,
  SortingDataInterface,
  TableProps,
} from '../types/propType';
import Pagination from '../Pagination/Pagination';
import { TableBody, TableHeader } from '../TableComponents';
import { handleSorting } from '../tableUtils';
import { TableRowType } from '../types/enums';
import styles from './AdminTable.module.scss';

const cx = classNames.bind(styles);

const Container = ({ children, containerClassName }: { children: React.ReactNode; containerClassName?: string }) =>
  containerClassName ? <div className={containerClassName}>{children}</div> : <Fragment>{children}</Fragment>;

const AdminTable = ({
  columns,
  dataSource,
  rowKey,
  onRowClick,
  selected,
  setSelectedKeys,
  selectedKeys = [],
  className,
  containerClassName,
  pagination = false,
  theme,
  sortingCallback,
  paginationObject,
  paginationCallback,
  enableCheckbox = false,
  enableRadioButton = false,
  defaultCheckedAllRows = true,
  formatPaginationCallback,
  forwardedRef,
  disabledItems,
  disabledCheckboxes,
  disabledRadioButtons,
  mlpVersion = MLPVersion.ONE,
  tableVersion,
  adminPaginationStyle,
  topAlignedDataInRow,
  hasNextPage,
  pageSizeData,
  scrollToTopRefId,
  highlightNewRow,
  adminSortingCallback,
  paginationPosition = Position.CENTER,
  message,
  columnsWithOverflowVisible = [],
  shouldFocusRow,
  showHeader = true,
  tableRowType = TableRowType.DEFAULT,
  tableFooter,
  enableStickyHeaderAndPagination = false,
  enableSlider = false,
  isLoading = false,
  ...rest
}: TableProps) => {
  const initialPagination = paginationObject || {
    totalRecords: dataSource.length,
    rowsPerPage: 10,
    currentPage: 0,
    recordStartIndex: 0,
    recordEndIndex: 10,
  };
  const [paginationData, setPaginationData] = useState<PaginationDataInterface>(initialPagination);
  const [sortingData, setSortingData] = useState<SortingDataInterface>({});
  const [tableData, setTableData] = useState<DynamicObjectInterface[]>(dataSource);
  const [selectedItemKeys, setSelectedItemKeys] = useState<string[]>(selectedKeys);

  const handleSetSelectedKeys = useCallback(
    (keys: string[]) => {
      setSelectedItemKeys(keys);
      setSelectedKeys?.(keys);
    },
    [setSelectedKeys],
  );

  useEffect(() => {
    const sortData = () => {
      if (sortingCallback || adminSortingCallback) {
        return dataSource; // assuming the callback function directly updated the dataSource list
      } else {
        const sortedData = handleSorting(sortingData, dataSource);
        return sortedData;
      }
    };
    let processedData = sortData();
    if (highlightNewRow?.length) {
      processedData = processedData.map((row) => {
        const updateRow = { ...row };
        delete updateRow?.isHighlighted;
        if (highlightNewRow.find((item) => item === updateRow[rowKey as string])) {
          return { ...updateRow, isHighlighted: true };
        } else {
          return updateRow;
        }
      });
    }
    setTableData(processedData);

    setPaginationData(initialPagination);
  }, [dataSource]);

  const getWidth = (width = 0) => {
    if (width) {
      return { width: `${width}%` };
    }

    return {};
  };
  const rowExpandCallback = (rowData: DynamicObjectInterface, rowIndex: number) => {
    const allRowsNodeList = document.querySelectorAll(`tr[data-group="${rowIndex}"]`);

    // Convert NodeList to Array
    const allRowsArray = Array.from(allRowsNodeList);
    allRowsArray.forEach((row) => row.classList.add(styles['children-row-close']));
    const updateTableData = () =>
      setTableData((prev) =>
        prev.map((row) => {
          if (row.id === rowData.id) {
            return { ...row, isExpanded: !rowData.isExpanded };
          } else {
            return row;
          }
        }),
      );
    if (rowData.isExpanded) {
      setTimeout(() => {
        updateTableData();
      }, 350);
    } else {
      updateTableData();
    }
  };
  const tableCLS = cx(
    'table-container',
    {
      'row-compact': tableRowType === TableRowType.COMPACT,
      'isolated-stacking-context': enableStickyHeaderAndPagination,
      'mlp-three': mlpVersion === MLPVersion.THREE,
    },
    className,
  );
  return (
    <Container containerClassName={containerClassName as string}>
      <table id={'admin-table'} className={tableCLS} {...rest} data-testid="table-data-test-id" ref={forwardedRef}>
        {showHeader && (
          <TableHeader
            columns={columns}
            tableData={tableData}
            getWidth={getWidth}
            sortingData={sortingData}
            setSortingData={setSortingData}
            setTableData={setTableData}
            paginationData={paginationData}
            styles={styles}
            enableCheckbox={enableCheckbox}
            enableRadioButton={enableRadioButton}
            defaultCheckedAllRows={defaultCheckedAllRows}
            isServerSideFetch={!!paginationCallback}
            pagination={pagination}
            setSelectedKeys={handleSetSelectedKeys}
            selectedKeys={selectedItemKeys}
            {...optionalProp('disabledCheckboxes', disabledCheckboxes)}
            {...optionalProp('rowKey', rowKey)}
            {...optionalProp('theme', theme)}
            {...optionalProp('sortingCallback', sortingCallback)}
            {...optionalProp('adminSortingCallback', adminSortingCallback)}
            {...optionalProp('disabledItems', disabledItems)}
            enableStickyHeader={enableStickyHeaderAndPagination}
            enableSlider={enableSlider}
            slideBack={rest.slideBack}
            slideForward={rest.slideForward}
          />
        )}
        {tableData.length === 0 && message && (
          <tr>
            <td colSpan={columns.length + 1}>
              <div className={styles['message']} data-testid="table-msg-test-id">
                {message}
              </div>
            </td>
          </tr>
        )}
        <TableBody
          rowExpandCallback={rowExpandCallback}
          columns={columns}
          tableData={tableData}
          setTableData={setTableData}
          rowKey={rowKey ?? ''}
          pagination={pagination}
          paginationData={paginationData}
          getWidth={getWidth}
          styles={styles}
          isServerSideFetch={!!paginationCallback}
          columnsWithOverflowVisible={columnsWithOverflowVisible}
          tableVersion={tableVersion}
          mlpVersion={mlpVersion}
          tableFooter={tableFooter}
          enableSlider={enableSlider}
          isLoading={isLoading && tableRowType === TableRowType.COMPACT}
          enableCheckbox={enableCheckbox}
          enableRadioButton={enableRadioButton}
          selectedKeys={selectedItemKeys}
          setSelectedKeys={handleSetSelectedKeys}
          {...optionalProp('selected', selected)}
          {...optionalProp('onRowClick', onRowClick)}
          {...optionalProp('disabledItems', disabledItems)}
          {...optionalProp('disabledCheckboxes', disabledCheckboxes)}
          {...optionalProp('disabledRadioButtons', disabledRadioButtons)}
          {...optionalProp('topAlignedDataInRow', topAlignedDataInRow)}
          {...optionalProp('shouldFocusRow', shouldFocusRow)}
          {...optionalProp('scrollToTopRefId', scrollToTopRefId)}
        />
      </table>
      {pagination && tableData.length > 0 ? (
        <Pagination
          mlpVersion={mlpVersion}
          {...paginationData}
          {...(paginationObject || paginationData)}
          updatePaginationData={(updatedData) => {
            paginationCallback?.({
              ...paginationData,
              ...updatedData,
            });

            setPaginationData({
              ...paginationData,
              ...updatedData,
            });
          }}
          paginationPosition={paginationPosition}
          {...optionalProp('formatPaginationCallback', formatPaginationCallback)}
          {...optionalProp('adminPaginationStyle', adminPaginationStyle)}
          {...optionalProp('hasNextPage', hasNextPage)}
          {...optionalProp('pageSizeData', pageSizeData)}
          {...optionalProp('scrollToTopRefId', scrollToTopRefId)}
          enableStickyPagination={enableStickyHeaderAndPagination}
        />
      ) : (
        ''
      )}
    </Container>
  );
};
AdminTable.displayName = 'AdminTable';

export default AdminTable;
export type { PaginationDataInterface };
